import {ChevronDown, ChevronUp} from 'lucide-react'
import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {formatAttendanceModeLabel} from '../../../modes/attendance/attendanceExportBuilder'
import {AttendanceVisualReportHeader} from '../../../modes/attendance/attendanceReportExportTypes'
import {buildAttendanceVisualSummary} from '../../../modes/attendance/buildAttendanceVisualSummary'
import {buildVisualReportExportPayload} from '../../../modes/attendance/buildStudentAttendanceExportPayload'
import {AttendanceParsedContent} from '../../../modes/attendance/parseAttendanceAnswer'
import {getAttendanceModeLabelId} from '../../../modes/attendance/resolveAttendanceDisplayMode'
import AttendanceRecordsTable from '../AttendanceRecordsTable'
import AttendancePlainCard from './AttendancePlainCard'
import AttendanceReportExportButtons from './AttendanceReportExportButtons'
import AttendanceStatusBars from './AttendanceStatusBars'
import AttendanceStatusPills from './AttendanceStatusPills'
import AttendanceSummaryRing from './AttendanceSummaryRing'

export type {AttendanceVisualReportHeader} from '../../../modes/attendance/attendanceReportExportTypes'

type AttendanceVisualReportProps = {
  content: AttendanceParsedContent
  theme: 'light' | 'dark'
  header?: AttendanceVisualReportHeader
  exportFilenameBase?: string
}

function formatHeaderDateRange(start?: string, end?: string): string | null {
  const s = start?.trim()
  const e = end?.trim()
  if (s && e) return `${s} – ${e}`
  if (s) return s
  if (e) return e
  return null
}

const AttendanceVisualReport: React.FC<AttendanceVisualReportProps> = ({
  content,
  theme,
  header,
  exportFilenameBase = 'attendance-report',
}) => {
  const intl = useIntl()
  const [showDetails, setShowDetails] = useState(true)

  useEffect(() => {
    setShowDetails(true)
  }, [content])

  const summary = useMemo(() => buildAttendanceVisualSummary(content), [content])

  const buttonMuted = theme === 'dark' ? 'rgba(255,255,255,0.85)' : '#6c757d'
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const cardBg = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.65)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.8)' : '#6c757d'

  const dateRangeLine = formatHeaderDateRange(header?.startDate, header?.endDate)

  const attendanceModeLabel = useMemo(
    () =>
      formatAttendanceModeLabel(
        header?.attendanceMode,
        intl.formatMessage,
        getAttendanceModeLabelId
      ),
    [header?.attendanceMode, intl]
  )

  const studentIdLine = header?.studentId
    ? `${intl.formatMessage({id: 'CHATBOT.ATTENDANCE.STUDENT_ID_LABEL'})}: ${header.studentId}`
    : null

  const metaParts = [
    studentIdLine,
    header?.branch,
    header?.className,
    header?.section,
    dateRangeLine,
    attendanceModeLabel,
  ].filter(Boolean)

  const exportPayload = useMemo(
    () =>
      buildVisualReportExportPayload({
        header,
        studentId: header?.studentId,
        content,
        exportFilenameBase,
        defaultTitle: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.EXPORT_REPORT_TITLE'}),
        attendanceModeLabel,
      }),
    [attendanceModeLabel, content, exportFilenameBase, header, intl]
  )

  if (!summary) return null

  const {mode, statusSegments, presentPercent, statPairs, totalRecords} = summary
  const showRing = mode !== 'stats' && statusSegments.length > 0

  return (
    <div
      className='attendance-visual-report mt-2 mb-1 rounded-3 p-2'
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${borderColor}`,
        maxWidth: '100%',
      }}
    >
      {header?.studentName ? (
        <div className='mb-2 pb-2' style={{borderBottom: `1px solid ${borderColor}`}}>
          <div style={{fontWeight: 700, fontSize: '0.9rem', color: textColor}}>
            {header.studentName}
          </div>
          {metaParts.length > 0 ? (
            <div style={{fontSize: '0.75rem', color: mutedColor, marginTop: 2}}>
              {metaParts.join(' · ')}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'stats' ? (
        <AttendancePlainCard pairs={statPairs} theme={theme} />
      ) : (
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          {showRing ? (
            <AttendanceSummaryRing
              segments={statusSegments}
              presentPercent={presentPercent}
              totalRecords={totalRecords}
              theme={theme}
            />
          ) : null}
          <AttendanceStatusBars segments={statusSegments} theme={theme} />
          <div className='flex-grow-1 min-w-0' style={{minWidth: 120}}>
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: textColor,
                marginBottom: 4,
              }}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.RECORDS_COUNT'}, {count: totalRecords})}
            </div>
            <AttendanceStatusPills segments={statusSegments} theme={theme} />
          </div>
        </div>
      )}

      <div className='d-flex justify-content-between align-items-center mt-2 gap-2 flex-wrap'>
        <button
          type='button'
          className='btn btn-sm btn-light d-inline-flex align-items-center gap-1'
          onClick={() => setShowDetails((v) => !v)}
          style={{
            fontSize: '0.75rem',
            color: buttonMuted,
            borderColor,
          }}
        >
          {showDetails ? (
            <ChevronUp size={14} aria-hidden />
          ) : (
            <ChevronDown size={14} aria-hidden />
          )}
          {intl.formatMessage({
            id: showDetails ? 'CHATBOT.ATTENDANCE.HIDE_DETAILS' : 'CHATBOT.ATTENDANCE.SHOW_DETAILS',
          })}
        </button>
        <div className='d-flex gap-1 ms-auto flex-wrap'>
          <AttendanceReportExportButtons exportPayload={exportPayload} theme={theme} />
        </div>
      </div>

      {showDetails ? (
        <div className='mt-2'>
          <AttendanceRecordsTable content={content} theme={theme} embedded hideCsv />
        </div>
      ) : null}
    </div>
  )
}

export default AttendanceVisualReport
